import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import type { Page } from '../../crm/client/pager'
import { useIsNarrow } from '../../hooks/useIsNarrow'
import { useLibraryDeps } from '../LibraryDeps'
import { ListLoading } from '../ListLoading'
import { Sentinel } from '../Sentinel'
import { SortMenu } from '../SortMenu'
import { useDebounced } from '../useDebounced'
import { useGridSelect } from '../useGridSelect'
import { useMediaSrc } from '../useMediaSrc'
import { usePagedList } from '../usePagedList'
import { MediaViewer } from '../vault/MediaViewer'
import { ALL_MEDIA, totalOf, type Category, type MediaType, type VaultItem } from '../vault/vaultData'
import { AudioArt, PickerTile } from './PickerTile'
import { forgetPages } from './pickerCache'
import { categoriesPage, mediaPage, type PickerSort } from './pickerQueries'
import { useLazyNotes } from './useLazyNotes'
import { badgesFor, useBadgeCatalog } from '../vault/badges'
import type { Badge } from '../LibraryDeps'
import '../library.css'
import './contentPicker.css'

/** Media the opener already has attached: an id and whatever else it knows. */
export type PickedMedia = Pick<VaultItem, 'id'> & Partial<VaultItem>

export interface ContentPickerProps {
  /** Keys the short-lived page cache; the media itself comes from LibraryDeps. */
  accountId: string
  /** Already attached, in order: shown picked and kept unless taken out. */
  initial?: PickedMedia[]
  onDone: (items: VaultItem[]) => void
  onClose: () => void
  /** Header title; "Add from Vault" by default. */
  title?: string
  /** At most this many may be picked (e.g. OnlyFans' 40 per post). */
  max?: number
  /** Only these kinds can be attached here; the rest are not shown. */
  types?: MediaType[]
}

const ALL_TYPES: MediaType[] = ['photo', 'video', 'audio', 'gif']

const SORTS = [
  { key: 'newest', labelKey: 'vault.sort.newest' },
  { key: 'oldest', labelKey: 'vault.sort.oldest' },
  { key: 'liked', labelKey: 'vault.sort.liked' },
] as const

const DENSITY_KEY = 'contentPicker.density'
type Density = 'compact' | 'default' | 'large'
const DENSITY_COL: Record<Density, string> = { compact: '132px', default: '164px', large: '212px' }

function useStoredDensity() {
  const [density, setDensity] = useState<Density>(() => {
    try {
      const v = localStorage.getItem(DENSITY_KEY)
      return v === 'compact' || v === 'large' ? v : 'default'
    } catch {
      return 'default'
    }
  })
  const choose = (d: Density) => {
    setDensity(d)
    try {
      localStorage.setItem(DENSITY_KEY, d)
    } catch {
      /* private mode: just this session */
    }
  }
  return [density, choose] as const
}

const categoryId = (c: Category) => c.id
const itemId = (m: VaultItem) => m.id

const asItem = (m: PickedMedia): VaultItem => ({
  type: 'photo',
  createdAt: '',
  duration: 0,
  thumb: '',
  full: '',
  inCategories: [],
  addableTo: [],
  ...m,
})

/**
 * The one content picker for OnlyFans accounts (chat, posts, mass messages):
 * the account's Vault by Category, with many-at-once picking (drag a box,
 * Shift-click a range, ⌘A), the pick order numbered, video lengths, audio
 * marked as audio, and operators' notes and Badges (read-only) loaded after the grid.
 */
export function ContentPicker({ accountId, initial = [], onDone, onClose, title, max, types }: ContentPickerProps) {
  const { t } = useTranslation('contentPicker')
  const { gateway, notes: notesApi, badges: badgesApi } = useLibraryDeps()
  const narrow = useIsNarrow()
  const typesKey = types?.length ? types.join() : ''
  const allowed = useMemo(() => (typesKey ? (typesKey.split(',') as MediaType[]) : ALL_TYPES), [typesKey])
  const [listId, setListIdRaw] = useState(ALL_MEDIA)
  const [type, setType] = useState<MediaType | null>(allowed.length === 1 ? allowed[0]! : null)
  const [sort, setSort] = useState<PickerSort>('newest')
  const [density, setDensity] = useStoredDensity()
  const [q, setQ] = useState('')
  const query = useDebounced(q.trim())
  const [all, setAll] = useState<Category | null>(null)
  const [generation, setGeneration] = useState(0)
  const [picked, setPicked] = useState<VaultItem[]>(() => initial.map(asItem))
  const [viewing, setViewing] = useState<number | null>(null)
  const [notice, setNotice] = useState('')
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const anchor = useRef<number | null>(null)

  const setListId = (id: string) => {
    setListIdRaw(id)
    setType(allowed.length === 1 ? allowed[0]! : null)
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }

  const fetchCategories = useCallback(
    async (offset: number): Promise<Page<Category>> => {
      const page = await categoriesPage(gateway, accountId, offset, query)
      if (page.all) setAll(page.all)
      return page
    },
    // generation: Refresh starts over.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gateway, accountId, query, generation],
  )
  const cats = usePagedList(fetchCategories, categoryId)

  const fetchMedia = useCallback(
    (offset: number) => mediaPage(gateway, accountId, listId, offset, type, sort),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gateway, accountId, listId, type, sort, generation],
  )
  const media = usePagedList(fetchMedia, itemId)
  const items = useMemo(() => (type || allowed.length === ALL_TYPES.length ? media.items : media.items.filter((m) => allowed.includes(m.type))), [media.items, type, allowed])
  const { notes, badges: itemBadges, observe } = useLazyNotes(notesApi)
  const catalog = useBadgeCatalog(badgesApi)
  // Stable per item, so memoised tiles only re-render when their own badges change.
  const tileBadges = useMemo(() => {
    const out: Record<number, Badge[]> = {}
    for (const [id, ids] of Object.entries(itemBadges)) out[Number(id)] = badgesFor(ids, catalog.badges)
    return out
  }, [itemBadges, catalog.badges])

  // Picks the opener knew only by id become the full item once it is loaded.
  useEffect(() => {
    const loaded = new Map(media.items.map((m) => [m.id, m]))
    setPicked((prev) => {
      if (!prev.some((m) => !m.createdAt && loaded.has(m.id))) return prev
      return prev.map((m) => (!m.createdAt && loaded.get(m.id)) || m)
    })
  }, [media.items])

  const order = useMemo(() => new Map(picked.map((m, i) => [m.id, i + 1])), [picked])
  const pickedIds = useMemo(() => picked.map((m) => m.id), [picked])
  const keys = useMemo(() => items.map((m) => m.id), [items])

  // Tiles get stable handlers that read the latest state from here.
  const live = useRef({ picked, items, keys })
  live.current = { picked, items, keys }

  /**
   * The new pick list, in the order of `next` (a drag adds in grid order);
   * adds respect `max` (what does not fit is left out, with a note why).
   */
  const pickIds = useCallback(
    (next: number[]) => {
      const before = new Map(live.current.picked.map((m) => [m.id, m]))
      const shown = new Map(live.current.items.map((m) => [m.id, m]))
      const keep = next.filter((id) => before.has(id)).length
      let room = max ? Math.max(0, max - keep) : Infinity
      let cut = false
      const result: VaultItem[] = []
      for (const id of new Set(next)) {
        const had = before.get(id)
        if (had) result.push(had)
        else if (shown.has(id) && room > 0) {
          result.push(shown.get(id)!)
          room -= 1
        } else if (shown.has(id)) cut = true
      }
      if (cut) setNotice(t('max', { count: max }))
      live.current.picked = result
      setPicked(result)
    },
    [max, t],
  )
  const toggle = useCallback(
    (m: VaultItem) => {
      anchor.current = m.id
      const ids = live.current.picked.map((x) => x.id)
      pickIds(ids.includes(m.id) ? ids.filter((id) => id !== m.id) : [...ids, m.id])
    },
    [pickIds],
  )
  const onTileClick = useCallback(
    (m: VaultItem, e: React.MouseEvent) => {
      const { keys: shownKeys } = live.current
      const from = anchor.current === null ? -1 : shownKeys.indexOf(anchor.current)
      const to = shownKeys.indexOf(m.id)
      if (!e.shiftKey || from < 0 || to < 0) return toggle(m)
      const ids = live.current.picked.map((x) => x.id)
      const span = shownKeys.slice(Math.min(from, to), Math.max(from, to) + 1)
      anchor.current = m.id
      pickIds([...ids, ...span.filter((id) => !ids.includes(id))])
    },
    [pickIds, toggle],
  )
  const preview = useCallback((m: VaultItem) => setViewing(live.current.items.findIndex((x) => x.id === m.id)), [])
  const selectShown = () => pickIds([...pickedIds, ...keys.filter((id) => !pickedIds.includes(id))])
  const allShownPicked = keys.length > 0 && keys.every((id) => order.has(id))
  const unpickShown = () => pickIds(pickedIds.filter((id) => !keys.includes(id)))

  const drag = useGridSelect({ scrollRef, keys, picked: pickedIds, onPick: pickIds, enabled: true, selecting: true, additive: true })

  const done = useCallback(() => onDone(picked), [onDone, picked])

  // Esc closes (the viewer first), Enter attaches, ⌘A picks everything shown.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (viewing !== null) return
      const typing = (e.target as HTMLElement | null)?.closest?.('input, textarea, [contenteditable="true"]')
      if (e.key === 'Escape') {
        e.stopPropagation()
        if (typing && q) return setQ('')
        onClose()
      } else if (typing) return
      else if (e.key === 'Enter' && !(e.target as HTMLElement | null)?.closest?.('button')) {
        e.preventDefault()
        done()
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault()
        selectShown()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  })

  useEffect(() => {
    if (!notice) return
    const id = window.setTimeout(() => setNotice(''), 3500)
    return () => window.clearTimeout(id)
  }, [notice])

  const refresh = () => {
    forgetPages(accountId)
    setGeneration((g) => g + 1)
  }

  const open = listId === ALL_MEDIA ? all : cats.items.find((c) => c.id === listId) ?? null
  const counts = open?.counts
  const chips = [null, ...allowed].filter((k) => k === null || !counts || counts[k] > 0 || k === type) as (MediaType | null)[]
  const summary = summarize(picked, t)

  const rail = (
    <aside className="cpk-rail" aria-label={t('categories')}>
      <label className="cpk-search">
        <FeatherIcon name="search" size={14} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('searchCategories')} aria-label={t('searchCategories')} />
      </label>
      <nav className="cpk-cats" aria-label={t('categories')}>
        {!query && (
          <CategoryRow name={t('allMedia')} count={all ? totalOf(all.counts) : null} on={listId === ALL_MEDIA} onOpen={() => setListId(ALL_MEDIA)} />
        )}
        {cats.items.map((c) => (
          <CategoryRow key={c.id} name={c.name} count={totalOf(c.counts)} on={listId === c.id} onOpen={() => setListId(c.id)} />
        ))}
        <ListLoading loading={cats.loading} empty={!cats.items.length} />
        {!cats.loading && query && !cats.items.length && <p className="cpk-empty">{t('noCategories')}</p>}
        {cats.hasMore && !cats.failed && <Sentinel onVisible={cats.loadMore} token={cats.items.length} />}
      </nav>
    </aside>
  )

  const viewer =
    viewing !== null && items[viewing] ? (
      <MediaViewer
        items={items}
        index={viewing}
        onIndex={setViewing}
        onClose={() => setViewing(null)}
        hasMore={media.hasMore}
        onLoadMore={media.loadMore}
        selected={order.has(items[viewing]!.id)}
        onToggle={() => toggle(items[viewing]!)}
        mobile={narrow}
        notes={notes[items[viewing]!.id] ? { value: notes[items[viewing]!.id]! } : undefined}
        badges={
          tileBadges[items[viewing]!.id]?.length
            ? { catalog, badges: tileBadges[items[viewing]!.id]!, stateOf: () => 'on' }
            : undefined
        }
      />
    ) : null

  return createPortal(
    <div className="cpk-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`cpk ${narrow ? 'cpk--narrow' : ''}`} role="dialog" aria-modal="true" aria-label={title ?? t('title')}>
        <header className="cpk-head">
          <span className="cpk-head-icon">
            <FeatherIcon name="archive" size={17} />
          </span>
          <div className="cpk-head-text">
            <h2 className="cpk-title">{title ?? t('title')}</h2>
            <span className="cpk-hint">{narrow ? t('hintTouch') : t('hint')}</span>
          </div>
          <button type="button" className="lib-icon-btn" aria-label={t('refresh')} title={t('refresh')} onClick={refresh}>
            <FeatherIcon name="rotate-cw" size={15} />
          </button>
          <button type="button" className="lib-icon-btn" aria-label={t('close')} title={t('close')} onClick={onClose}>
            <FeatherIcon name="x" size={18} />
          </button>
        </header>
        <div className="cpk-body">
          {narrow ? (
            <div className="cpk-catstrip" role="radiogroup" aria-label={t('categories')}>
              <button type="button" role="radio" aria-checked={listId === ALL_MEDIA} className={`lib-chip ${listId === ALL_MEDIA ? 'is-on' : ''}`} onClick={() => setListId(ALL_MEDIA)}>
                {t('allMedia')}
              </button>
              {cats.items.map((c) => (
                <button key={c.id} type="button" role="radio" aria-checked={listId === c.id} className={`lib-chip ${listId === c.id ? 'is-on' : ''}`} onClick={() => setListId(c.id)}>
                  {c.name}
                </button>
              ))}
              {cats.hasMore && !cats.loading && (
                <button type="button" className="lib-chip" onClick={() => void cats.loadMore()}>
                  {t('more')}
                </button>
              )}
            </div>
          ) : (
            rail
          )}
          <main className="cpk-main">
            <div className="cpk-toolbar">
              <div className="lib-chips" role="radiogroup" aria-label={t('typeFilter')}>
                {chips.map((k) => (
                  <button
                    key={k ?? 'all'}
                    type="button"
                    role="radio"
                    aria-checked={type === k}
                    className={`lib-chip ${type === k ? 'is-on' : ''}`}
                    onClick={() => {
                      setType(k)
                      if (scrollRef.current) scrollRef.current.scrollTop = 0
                    }}
                  >
                    {t(k ? `chip.${k}` : 'chip.all')}
                    {counts && <span className="lib-chip-n">{(k ? counts[k] : totalOf(counts)).toLocaleString()}</span>}
                  </button>
                ))}
              </div>
              <span className="lib-spacer" />
              {keys.length > 0 &&
                (narrow ? (
                  <button
                    type="button"
                    className="lib-icon-btn"
                    aria-label={allShownPicked ? t('unselectShown') : t('selectShown', { count: keys.length })}
                    aria-pressed={allShownPicked}
                    onClick={allShownPicked ? unpickShown : selectShown}
                  >
                    <FeatherIcon name={allShownPicked ? 'minus-square' : 'check-square'} size={16} />
                  </button>
                ) : (
                  <button type="button" className="lib-btn lib-btn--ghost" onClick={allShownPicked ? unpickShown : selectShown}>
                    {allShownPicked ? t('unselectShown') : t('selectShown', { count: keys.length })}
                  </button>
                ))}
              <SortMenu options={SORTS} value={sort} onChange={setSort} density={narrow ? undefined : density} onDensity={narrow ? undefined : setDensity} />
            </div>
            <div ref={scrollRef} className="cpk-scroll lib-scroll" {...drag.rootProps} style={{ ['--cpk-col' as string]: DENSITY_COL[density], ...drag.rootProps.style }}>
              {drag.box && <div className="lib-dragbox" style={{ left: drag.box.left, top: drag.box.top, width: drag.box.width, height: drag.box.height }} />}
              <div className="cpk-grid" role="listbox" aria-multiselectable="true" aria-label={open?.name || t('allMedia')}>
                {items.map((m) => (
                  <PickerTile key={m.id} item={m} order={order.get(m.id) ?? 0} note={notes[m.id]} badges={tileBadges[m.id]} onClick={onTileClick} onToggle={toggle} onPreview={preview} observe={observe} />
                ))}
              </div>
              <ListLoading loading={media.loading} empty={!items.length} />
              {!media.loading && !media.failed && !items.length && <p className="cpk-empty">{t('empty')}</p>}
              {media.failed && (
                <p className="cpk-empty">
                  {t('error')}{' '}
                  <button type="button" className="lib-btn lib-btn--ghost cpk-inline" onClick={() => void media.loadMore()}>
                    {t('retry')}
                  </button>
                </p>
              )}
              {media.hasMore && !media.failed && <Sentinel onVisible={media.loadMore} token={items.length} />}
            </div>
          </main>
        </div>
        <footer className="cpk-foot">
          <Tray items={picked} onRemove={(m) => pickIds(pickedIds.filter((id) => id !== m.id))} />
          <div className="cpk-foot-side">
            <span className="cpk-summary" aria-live="polite">
              {notice || summary}
            </span>
            {picked.length > 0 && (
              <button type="button" className="lib-btn lib-btn--ghost" onClick={() => pickIds([])}>
                {t('clear')}
              </button>
            )}
            <button type="button" className="lib-btn" onClick={onClose}>
              {t('cancel')}
            </button>
            <button type="button" className="lib-btn lib-btn--primary cpk-attach" onClick={done}>
              {picked.length ? t('attach', { count: picked.length }) : initial.length ? t('attachNone') : t('done')}
            </button>
          </div>
        </footer>
      </div>
      {viewer}
    </div>,
    document.body,
  )
}

function summarize(items: VaultItem[], t: TFunction): string {
  if (!items.length) return t('noneSelected')
  const n = { photo: 0, video: 0, audio: 0, gif: 0 }
  let seconds = 0
  for (const m of items) {
    n[m.type] += 1
    if (m.type === 'video' || m.type === 'audio') seconds += m.duration
  }
  const parts = (['photo', 'video', 'audio', 'gif'] as const).filter((k) => n[k]).map((k) => t(`count.${k}`, { count: n[k] }))
  const s = Math.round(seconds)
  if (s > 0) parts.push(t('totalLength', { time: `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` }))
  return parts.join(' · ')
}

function CategoryRow({ name, count, on, onOpen }: { name: string; count: number | null; on: boolean; onOpen: () => void }) {
  return (
    <button type="button" className={`cpk-cat ${on ? 'is-on' : ''}`} aria-current={on || undefined} onClick={onOpen}>
      <FeatherIcon name="folder" size={14} />
      <span className="cpk-cat-name">{name}</span>
      {count !== null && <span className="cpk-cat-n">{count.toLocaleString()}</span>}
    </button>
  )
}

/** The picks in order along the footer; × takes one out. */
function Tray({ items, onRemove }: { items: VaultItem[]; onRemove: (m: VaultItem) => void }) {
  const { t } = useTranslation('contentPicker')
  const strip = useRef<HTMLDivElement | null>(null)
  const count = items.length
  // A new pick scrolls the strip to the end, where it was added.
  useEffect(() => {
    const el = strip.current
    if (el) el.scrollLeft = el.scrollWidth
  }, [count])
  return (
    <div ref={strip} className="cpk-tray" aria-label={t('selectedList')}>
      {items.map((m, i) => (
        <TrayThumb key={m.id} item={m} n={i + 1} onRemove={() => onRemove(m)} />
      ))}
    </div>
  )
}

function TrayThumb({ item, n, onRemove }: { item: VaultItem; n: number; onRemove: () => void }) {
  const { t } = useTranslation('contentPicker')
  const { ref, src } = useMediaSrc(item.type === 'audio' ? '' : item.small || item.thumb)
  return (
    <span ref={ref as React.RefObject<HTMLSpanElement>} className="cpk-tray-item" title={t(`type.${item.type}`)}>
      {item.type === 'audio' ? <AudioArt id={item.id} compact /> : src ? <img src={src} alt="" draggable={false} /> : <span className="cpk-tray-ph" />}
      {item.type === 'video' && <FeatherIcon name="play" size={10} className="cpk-tray-kind" />}
      <span className="cpk-tray-n">{n}</span>
      <button type="button" className="cpk-tray-x" aria-label={t('remove')} onClick={onRemove}>
        <FeatherIcon name="x" size={10} />
      </button>
    </span>
  )
}
